import { Injectable } from '@angular/core';
import { SexLangPipe } from '../../shared/pipes/sex-lang.pipe';
import { CalculateAgePipe } from '../../shared/pipes/calculate-age.pipe';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { LOCALE_ID, Inject, PLATFORM_ID } from '@angular/core';
import { DatabaseCacheService } from '../cache/database-cache.service';
import { isPlatformBrowser } from '@angular/common';
import { PDF_SERVICE_I18N } from './pdf.service.i18n';

const MS_MINCHO_FONT_NAME = 'msmincho.ttf';
const MS_MINCHO_FONT_URL = 'assets/fonts/MSMINCHO.TTF';
const TC_FONT_NAME = 'NotoSansCJKtc-Regular.otf';
const TC_FONT_URL = 'assets/fonts/NotoSansCJKtc-Regular.otf';
pdfMake.vfs = { ...(pdfFonts as any)?.pdfMake?.vfs ?? {} };

@Injectable({
  providedIn: 'root'
})
export class PdfService {
  private fontLoadPromise: Promise<void> | null = null;

  constructor(
    private common: DatabaseCacheService,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(LOCALE_ID) public locale: string,
  ) { }

  //generate pdf from json data
  async generatePdf(data) {
    await this.ensurePdfFonts();
    const documentDefinition = { content: data, defaultStyle: { font: this.pdfFontFamily } };
    pdfMake.createPdf(documentDefinition).open();
  }

  //download pdf from json data
  async downloadPdf(user, section, format, data, filename, chartImage?) {
    await this.ensurePdfFonts();
    const sections = [];
    let language = this.locale;

    let layout = {
      hLineWidth: function (i, node) {
        return 0.5;
      },
      vLineWidth: function (i, node) {
        return (i === 0 || i === node.table.widths.length) ? 0.5 : 0.5;
      }
    };

    let userProfilelayoutTop = {
      hLineWidth: function (i, node) {
        if (i === 0) {
          return 0.5;
        }
        return 0;
      },
      vLineWidth: function (i, node) {
        return 0.5;
      }
    };

    let userProfilelayoutBottom = {
      hLineWidth: function (i, node) {
        return 0.5;
      },
      vLineWidth: function (i, node) {
        return 0.5;
      }
    };

    let isFirstRenderedRow = true;
    for (let s = 0; s < format.length; s++) {
      const section = format[s];
      const column1 = [];
      const column2 = [];
      const columnHeader = {};
      const headerItems = ["Header1", "Header2", "Header3"];
      const midIndex = Math.ceil(section.dataItems.filter(x=>!headerItems.includes(x.type)).length / 2);
      let normalItemIndex = 0;
      let headerTable = null;

      for (let i = 0; i < section.dataItems.length; i++) {
        const item = section.dataItems[i];
        const itemName = this.common.getDispName(item);
        let itemValue = data.data[item.name];
        if (item.enabled == true) {

          if (item.type === "Bool") {
            // If the item is a boolean, represent it with a checkbox-like string
            itemValue = (itemValue === true || itemValue === 'true') ? '☑' : '☐';
          } else if (itemValue !== undefined && itemValue !== null && itemValue !== '') {
            const listSource = item.values?.length ? item.values : item.listValues;
            if (listSource?.length) {
              itemValue = this.common.getListItemDispValue(listSource, itemValue);
            }
          }

          // Noto CJK has square markers but no ballot-box glyphs.
          if (this.usesTraditionalChineseFont && typeof itemValue === 'string') {
            itemValue = itemValue.replace(/☑/g, '■').replace(/☐/g, '□');
          }

          let textObj = {};

          if (section.name === 'KTChart') {
            textObj = {
              columns: [
                {
                  text: itemName,
                  fontSize: itemName.length > 9 ? 7 : 8.2,
                  width: '72%',
                  margin: [0, 0, 0, 0],
                  noWrap: true,
                  alignment: 'left',
                },
                {
                  text: itemValue !== undefined && itemValue !== null && itemValue !== '' ? itemValue : ' ',
                  fontSize: 9,
                  width: '28%',
                  margin: [0, 0, 0, 0],
                  noWrap: true,
                  alignment: 'left',
                },
              ],
              margin: [0, 0, 0, 3],
              columnGap: 6,
            };

          }
          else if (section.name === 'comment') {
              textObj = {
                columns: [
                  {
                    text: itemName,
                    fontSize: itemName.length > 9 ? 7 : 9,
                    width: '20%',  // itemName の幅を30%に設定
                    margin: [0, 0, 3, 0]
                  },
                  {
                    text: itemValue !== undefined && itemValue !== null && itemValue !== '' ? itemValue : ' ',
                    fontSize: 8,
                    width: '80%',  // itemValue の幅を70%に設定
                    margin: [0, 0, 0, 0]
                  }
                ],
                margin: [0, 0, 0, 5]  // テキスト間の余白
              };

          }
          else if (section.visualizationType === 'Record' || section.visualizationType === 'RecordDetail') {
            if (item.type == "Header1") {
              textObj = {
                columns: [
                  {
                    text: itemName,
                    fontSize: 9,
                    width: '100%',
                    margin: [0, 0, 0, 0]
                  }
                ],
                margin: [0, 0, 0, 5]  // テキスト間の余白
              };
            } else if (headerItems.includes(item.type)) {
              textObj = {
                columns: [
                  {
                    text: itemName,
                    fontSize: itemName.length > 9 ? 7 : 9,
                    width: '38%',
                    margin: [0, 0, 3, 0]
                  },
                  {
                    text: itemValue !== undefined && itemValue !== null && itemValue !== '' ? itemValue : ' ',
                    fontSize: (itemValue && itemValue.length > 10) ? 7 : 9,
                    width: '62%',
                    margin: [0, 0, 0, 0]
                  }
                ],
                margin: [0, 0, 0, 5]  // テキスト間の余白
              };
            } else if (section.visualizationSubType === 'Wide') {
              textObj = {
                columns: [
                  {
                    text: itemName,
                    fontSize: 9,
                    width: '20%',
                    margin: [0, 0, 3, 0]
                  },
                  {
                    text: itemValue !== undefined && itemValue !== null && itemValue !== '' ? itemValue : ' ',
                    fontSize: 9,
                    width: '80%',
                    margin: [0, 0, 0, 0]
                  }
                ],
                margin: [0, 0, 0, 5]  // テキスト間の余白
              };
            } else {
              textObj = {
                columns: [
                  {
                    text: itemName,
                    fontSize: itemName.length > 12 ? 7 : 9,
                    width: '42%',
                    margin: [0, 0, 3, 0]
                  },
                  {
                    text: itemValue !== undefined && itemValue !== null && itemValue !== '' ? itemValue : ' ',
                    fontSize: (itemValue && itemValue.length > 18) ? 7 : 9,
                    width: '58%',  // itemValue の幅を70%に設定
                    margin: [0, 0, 0, 0]
                  }
                ],
                margin: [0, 0, 0, 5]  // テキスト間の余白
              };
            }
          }
          else {
            // テキストオブジェクトを作成
            textObj = {
              columns: [
                {
                  text: itemName,
                  fontSize: itemName.length > 9 ? 7 : 9,
                  width: '42%',  // itemName の幅を30%に設定
                  margin: [0, 0, 3, 0]
                },
                {
                  text: itemValue !== undefined && itemValue !== null && itemValue !== '' ? itemValue : ' ',
                  fontSize: (itemValue && itemValue.length > 10) ? 7 : 9,
                  width: '58%',  // itemValue の幅を70%に設定
                  margin: [0, 0, 0, 0]
                }
              ],
              margin: [0, 0, 0, 5]  // テキスト間の余白
            };
          }

          if (headerItems.includes(item.type))
          {
            columnHeader[item.type] = textObj;

          } else {
            // 配列の半分までを column1 に、残りを column2 に分ける
            if (normalItemIndex < midIndex) {
              column1.push(textObj);
            } else {
              column2.push(textObj);
            }
            // ヘッダーの除くアイテムのインデックス
            normalItemIndex++;
          }

        }
      }

      const sectionTitle =
        this.common.getDispName(section) ??
        section.displayNames?.jp ??
        section.name ??
        '';
      const sectionDescription = section.description
        ? this.common.getDispName(section.description)
        : '';

      let columnContent;  // comment section has only one column

      // Calculate the difference in length between column1 and column2
      let lengthDifference = column1.length - column2.length;

      // If column1 is longer than column2
      if (lengthDifference > 0) {
        // Add empty items to column2
        for (let i = 0; i < lengthDifference; i++) {
          column2.push({ text: ' ', fontSize: 9, margin: [0, 0, 0, 5] });
        }
      } else if (lengthDifference < 0) {
        // If column2 is longer than column1, add empty items to column1
        for (let i = 0; i < Math.abs(lengthDifference); i++) {
          column1.push({ text: ' ', fontSize: 9, margin: [0, 0, 0, 5] });
        }
      }

      if (section.name === 'comment') {
        // For 'comment' section, add all items in a single column that takes 100% width
        columnContent = [{
          width: '100%',
          stack: [...column1, ...column2],
          margin: [0, 0, 5, 0]
        }];
      }
      else if (section.name === 'KTChart') {
        columnContent = [
          {
            width: '55%',
            stack: [{ image: chartImage, width: 240, border: [true, false, true, true] }],
          },
          {
            width: '22.5%',
            stack: column1,
            margin: [4, 0, 5, 0],
          },
          {
            width: '22.5%',
            stack: column2,
            margin: [8, 0, 0, 0],
          }
        ];
      }
      else if (section.visualizationType === 'Record' || section.visualizationType === 'RecordDetail') {
        if (Object.keys(columnHeader).length > 0) {
          columnContent = [
            {
              width: '52%',
              stack: [columnHeader["Header1"]],
            },
            {
              width: '24%',
              stack: [columnHeader["Header2"]],
            },
            {
              width: '24%',
              stack: [columnHeader["Header3"]],
            }
          ];
          headerTable = {
              columns: columnContent,
              border: [true, false, true, false]
          };
        }
        if (section.visualizationSubType === 'Wide'){
          columnContent = [{
            width: '100%',
            stack: [...column1, ...column2],
            margin: [0, 0, 5, 0]
          }];
        } else {
          columnContent = [
            {
              width: '50%',
              stack: column1,
              margin: [0, 0, 30, 0]
            },
            {
              width: '50%',
              stack: column2
            }
          ];
        }
      }
      else {
        // For other sections, split items into two columns
        columnContent = [
          {
            width: '50%',
            stack: column1,
            margin: [0, 0, 30, 0]
          },
          {
            width: '50%',
            stack: column2
          }
        ];
      }

      const body = [];
      const showSectionHeader =
        section.visualizationType !== 'RecordDetail' &&
        !section.__fromPdfOption;
      if (showSectionHeader && (sectionTitle || sectionDescription)) {
        const headerSegments: any[] = [];
        if (sectionTitle) {
          headerSegments.push({
            text: sectionTitle,
            bold: true,
            fontSize: 10,
          });
        }
        if (sectionDescription) {
          headerSegments.push({
            text: `　${sectionDescription}`,
            fontSize: 8,
          });
        }
        const headerTopBorder = isFirstRenderedRow || s === 0;
        body.push([
          {
            text: headerSegments,
            margin: [0, 0, 0, 5],
            border: [true, headerTopBorder ? true : false, true, false],
          },
        ]);
        isFirstRenderedRow = false;
      }
      if (headerTable) {
        body.push([{
          ...headerTable,
          border: [true, isFirstRenderedRow ? true : false, true, false],
        }]);
        isFirstRenderedRow = false;
      }

      const nextVisualizationType = s < format.length-1 ? format[s+1].visualizationType : null;
      const contentBorderTop = isFirstRenderedRow;
      body.push([{
        columns: columnContent,
        border: [true, contentBorderTop ? true : false, true, nextVisualizationType !== 'RecordDetail'],
      }]);
      isFirstRenderedRow = false;

      sections.push(
        {
          table: {
            widths: ['*'],
            body: body
          },
          layout: layout,
          margin: [0, 0, 0, 0]  // Modify the bottom margin as needed to adjust the space between tables
        }
      );
    }

    // 新しいセクションを作成
    //"createdAt": "2023-03-29T21:22:31.897+09:00"を取得して、日付のみを取得して、年月日のみを取得して、年月日を/区切りにして表示する
    const createdAt = data.asOfDate.split('T')[0].split('-').join('/');
    const isoString = new Date().toISOString();
    const datePart = isoString.split('T')[0];
    const today = datePart.split('-').join('/');
    const reportOutputDate = PDF_SERVICE_I18N.report.outputDate + today;

    const userName = user.user.userName;
    const userBirthday = user.user.birthDate.split('T')[0].split('-').join('/');
    const sexLangPipe = new SexLangPipe();
    const calculateAgePipe = new CalculateAgePipe();

    const userAge = calculateAgePipe.transform(user.user.birthDate);
    const userSex = sexLangPipe.transform(user.user.sex, 'jp');

    const titleSection = {
      table: {
        widths: ['*'],
        body: [
          [
            {
              text: PDF_SERVICE_I18N.report.title,
              fontSize: 16,
              alignment: 'center',
              border: [false, false, false, false]
            }
          ],
          [
            {
              text: reportOutputDate,
              fontSize: 9,
              alignment: 'right',
              border: [false, false, false, false]
            }
          ],
        ]
      },
      layout: 'noBorders'
    };

    const userProfileSectionTop = {
      table: {
        // widths: ['*'],
        widths: ['19%', '22%', '12%', '27%', '8%', '12%'],
        body: [
          [
            { text: PDF_SERVICE_I18N.report.chartNumber, fontSize: 9, border: [true, true, true, true], noWrap: true },
            { text: '', fontSize: 9, border: [true, true, true, true], noWrap: true, alignment: 'left' },
            { text: PDF_SERVICE_I18N.report.patientName, fontSize: 9, border: [true, true, true, true], noWrap: true },
            { text: userName, fontSize: 9, border: [true, true, true, true], noWrap: true, alignment: 'left' },
            { text: PDF_SERVICE_I18N.report.evaluationDate, fontSize: 9, border: [true, true, true, true], noWrap: true },
            { text: createdAt, fontSize: 9, border: [true, true, true, true], noWrap: true, alignment: 'left' }
          ]
        ]
      },
      layout: userProfilelayoutTop,
      margin: [0, 0, 0, 0],
      border: [true, true, true, false]
    };

    const userProfileSectionBottom = {
      table: {
        //何故かwidthsの値が上と同じにしても垂直の罫線がずれるので、細かく調整
        widths: ['19%', '22%', '12%', '27%', '8%', '12%'],
        body: [
          [
            { text: PDF_SERVICE_I18N.report.birthDate, fontSize: 9, border: [true, true, true, true], noWrap: true },
            { text: userBirthday, fontSize: 9, border: [true, true, true, true], noWrap: true, alignment: 'left' },
            { text: PDF_SERVICE_I18N.report.age, fontSize: 9, border: [true, true, true, true], noWrap: true },
            { text: userAge + PDF_SERVICE_I18N.report.ageUnit, fontSize: 9, border: [true, true, true, true], noWrap: true, alignment: 'left' },
            { text: PDF_SERVICE_I18N.report.sex, fontSize: 9, border: [true, true, true, true], noWrap: true },
            { text: userSex, fontSize: 9, border: [true, true, true, true], noWrap: true, alignment: 'left' },
          ]
        ]
      },
      layout: userProfilelayoutBottom,
      margin: [0, 0, 0, 20],
      border: [true, true, true, true]
    };

    sections.unshift(userProfileSectionBottom); // 患者情報セクションを最初に追加
    sections.unshift(userProfileSectionTop); // 患者情報セクションを最初に追加

    sections.unshift(titleSection); // タイトルセクションをそれよりも前に追加

    const addressText = {
      width: '100%',
      stack: [`${section.addresses.zip} ${section.addresses.prefecture} ${section.addresses.city} ${section.addresses.town} ${section.addresses.line1} ${section.addresses.line2}`],
      alignment: 'right',
      fontSize: 9,
      border: [false, false, false, false]
    };

    const sectionName = {
      width: '100%',
      stack: [section.sectionName],
      alignment: 'right',
      fontSize: 9,
      border: [false, false, false, false]
    };

    const signatureField = {
      table: {
        widths: ['55%', '15%', '30%'],
        body: [
          [
            { text: '', border: [false, false, false, false] },
            {
              text: PDF_SERVICE_I18N.report.signature,
              alignment: 'right',
              margin: [0, 10, 0, 0],
              fontSize: 9,
              noWrap: true,
              border: [false, false, false, false],
            },
            {
              canvas: [
                {
                  type: 'line',
                  x1: 0,
                  y1: 10,
                  x2: 150,
                  y2: 10,
                  lineWidth: 0.4,
                },
              ],
              margin: [0, 10, 0, 0],
              border: [false, false, false, false],
            },
          ],
        ],
      },
      border: [false, false, false, false],
      layout: 'noBorders',
      alignment: 'right',
    };

    // 住所テーブルを追加
    sections.push({
      table: {
        widths: ['*'],
        body: [
          [{
            table: {
              widths: ['*'],
              body: [
                [sectionName],
                [addressText],
                [signatureField]
              ]
            },
            border: [false, false, false, false]
          }]
        ]
      },
      layout: layout,
      margin: [0, 0, 0, 0]
    });

    const documentDefinition = {
      content: sections,
      defaultStyle: {
        font: this.pdfFontFamily
      }
    };
    const fonts = pdfMake.fonts;
    const vfs = (pdfMake as any)?.vfs;
    if (!vfs?.[this.pdfFontFile]) {
      throw new Error('PDF_FONT_MISSING');
    }
    pdfMake.createPdf(documentDefinition, undefined, fonts, vfs).download(filename);
  }

  private async ensurePdfFonts(): Promise<void> {
    if (this.fontLoadPromise) {
      return this.fontLoadPromise;
    }

    this.fontLoadPromise = (async () => {
      const fontsModule = (pdfFonts as any)?.pdfMake;
      const baseVfs = (pdfMake as any)?.vfs
        ? { ...(pdfMake as any).vfs }
        : fontsModule?.vfs
        ? { ...fontsModule.vfs }
        : {};

      if (!isPlatformBrowser(this.platformId)) {
        pdfMake.vfs = baseVfs;
        this.applyFontDefinitions();
        return;
      }

      try {
        const response = await fetch(this.pdfFontUrl);
        if (!response.ok) throw new Error(`PDF font request failed: ${response.status}`);
        const buffer = await response.arrayBuffer();
        const base64Font = this.arrayBufferToBase64(buffer);
        if (!base64Font?.length) {
          throw new Error('PDF font payload is empty.');
        }
        const customVfs = { [this.pdfFontFile]: base64Font };
        pdfMake.vfs = { ...baseVfs, ...customVfs };
      } catch (err) {
        console.error('[ERROR] PdfService > ensurePdfFonts()', err);
        pdfMake.vfs = baseVfs;
        throw err;
      }

      this.applyFontDefinitions();
    })();

    try {
      await this.fontLoadPromise;
    } catch (error) {
      this.fontLoadPromise = null;
      throw error;
    }
  }

  private get usesTraditionalChineseFont(): boolean {
    return /^(tw$|zh-(tw|hant)(-|$))/i.test(this.locale.replace(/_/g, '-'));
  }

  private get pdfFontFile(): string {
    return this.usesTraditionalChineseFont ? TC_FONT_NAME : MS_MINCHO_FONT_NAME;
  }

  private get pdfFontUrl(): string {
    return this.usesTraditionalChineseFont ? TC_FONT_URL : MS_MINCHO_FONT_URL;
  }

  private get pdfFontFamily(): string {
    return this.usesTraditionalChineseFont ? 'NotoSansTC' : 'MSMincho';
  }

  private applyFontDefinitions(): void {
    pdfMake.fonts = {
      ...pdfMake.fonts,
      [this.pdfFontFamily]: {
        normal: this.pdfFontFile,
        bold: this.pdfFontFile,
        italics: this.pdfFontFile,
        bolditalics: this.pdfFontFile
      }
    };
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
      binary += String.fromCharCode(...chunk);
    }
    return btoa(binary);
  }

}
